import { memo, MouseEvent, PropsWithChildren, ReactElement, useMemo } from 'react';
import {
    GridCellParams,
    GridColDef,
    GridRowParams,
    GridRowSelectionModel,
    GridValueGetterParams,
    MaterialDataGrid,
    GridRowModel,
    GridColumnVisibilityModel,
} from '@enterprise/material-data-grid';
import { PracticeInfo, PracticeStatus, SiteEntityInfo } from '../MyOrganizationPageStore';
import styles from './practiceGrid.module.scss';
import { SpotButton, SpotPill, SpotToggle } from '@enterprise/spot';
import i18n from '../../../i18n';
import classNames from 'classnames';
import { usePracticesGridSize } from './usePracticesGridSize.hook';
import { head } from 'lodash';

enum GridColumns {
    MobileView = 'mobileView',
    Name = 'name',
    Action = 'action',
    PIMS = 'pims',
    Labels = 'labels',
    PracticeStatus = 'practiceStatus',
}

const getPracticeName = ({ name, type }: PracticeInfo): string => {
    if (!name && type === PracticeStatus.Pending) {
        return i18n.t('myOrganization:defaultPendingPracticeName', 'Pending Practice Connection');
    }

    return String(name);
};

const MobileHeader = (): ReactElement => {
    // filter by practice name should goes here
    return (
        <div className={styles.headerContainer}>
            <div className={classNames('spot-typography__text--body', styles.headerCell)}>
                {i18n.t('myOrganization:practiceName', 'Practice Name')}
            </div>
        </div>
    );
};

const SyncButton = (props: PropsWithChildren<PracticesGridProps & { practice?: SiteEntityInfo }>): ReactElement => {
    const { isSyncPending, onSyncClick, groupBy, masterSiteId, practice, children } = props;
    const isSyncAvailable = groupBy === 'description' && (!practice || (practice.id === masterSiteId && practice.isEnabled && practice.isActive));
    const buttonTitle = isSyncPending ? i18n.t('myOrganization:syncProductsPending', 'Sync Pending') : children;

    const onclick = (event: MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        onSyncClick?.(practice ? [practice.id] : '*');
    };

    return (
        <>
            {isSyncAvailable && (
                <SpotButton onClick={onclick} disabled={isSyncPending} btnSize={'small'} isPrimary={true} icon={'refresh-redo'}>
                    {buttonTitle}
                </SpotButton>
            )}
        </>
    );
};

const mobileCell = (gridProps: PracticesGridProps) => {
    const StatusCell = practiceStatusCell(gridProps);

    return function MobileCell(params: GridCellParams): ReactElement {
        const name = getPracticeName(params.row as PracticeInfo);
        const { city, state, pimsName, labels } = params.row as SiteEntityInfo;
        const practiceInfo = [city, state].filter((item) => item).join(', ');
        const labelNames = labels?.map(({ name }) => name);

        return (
            <div className={styles.cellContainer}>
                <div className={styles.cellMobile}>
                    <div className={classNames(styles.mobilePracticeName)}>
                        <div className={classNames('spot-typography__heading--level-4', styles.name)} data-automation-id="practices-grid-cell-name">
                            {name}
                        </div>
                        {Boolean(practiceInfo) && (
                            <span
                                className={classNames('spot-typography__text--tertiary', styles.practiceInfo)}
                                data-automation-id="practices-grid-cell-practice-info"
                            >
                                {practiceInfo}
                            </span>
                        )}
                    </div>
                    <div className={classNames('spot-typography__text--body', styles.mobileInfo)}>
                        <span className={styles.pims} data-automation-id="practices-grid-cell-pims-name">
                            {pimsName}
                        </span>
                        {Boolean(labelNames?.length) && (
                            <span className={styles.labels} data-automation-id="practices-grid-cell-region-name">
                                {labelNames?.join(', ')}
                            </span>
                        )}
                    </div>
                    <StatusCell {...params} />
                </div>
            </div>
        );
    };
};

const nameHeader = () => {
    return function NameHeader(): ReactElement {
        // filter by practice name should goes here
        return (
            <div className={styles.headerContainer}>
                <div className={classNames('spot-typography__text--body', styles.headerCell)}>
                    {i18n.t('myOrganization:practiceName', 'Practice Name')}
                    {/*<SyncButton {...props}>{i18n.t('myOrganization:syncAll', 'Sync All')}</SyncButton>*/}
                </div>
            </div>
        );
    };
};

const actionCell = (props: PracticesGridProps) => {
    return function ActionCell(params: GridCellParams): ReactElement {
        const { id } = params.row as SiteEntityInfo;
        const title = props.masterSiteId === id ? i18n.t('myOrganization:syncBase', 'Sync Base') : i18n.t('myOrganization:syncPractice', 'Sync');

        return (
            <div className={styles.cellContainer}>
                <div className={styles.cell}>
                    <SyncButton {...props} practice={params.row as SiteEntityInfo}>
                        {title}
                    </SyncButton>
                </div>
            </div>
        );
    };
};

const actionHeader = (props: PracticesGridProps) => {
    return function ActionHeader(): ReactElement {
        // filter by practice name should goes here
        return (
            <div className={styles.headerContainer}>
                <div className={classNames('spot-typography__text--body', styles.headerCell)}>
                    <SyncButton {...props}>{i18n.t('myOrganization:syncAll', 'Sync All')}</SyncButton>
                </div>
            </div>
        );
    };
};

const desktopCell = () => {
    return function NameCell(params: GridCellParams): ReactElement {
        const name = getPracticeName(params.row as PracticeInfo);
        const { city, state } = params.row as SiteEntityInfo;
        const practiceInfo = [city, state].filter((item) => item).join(', ');

        return (
            <div className={styles.cellContainer}>
                <div className={styles.cellName}>
                    <div className={classNames('spot-typography__heading--level-4', styles.name)} data-automation-id="practices-grid-cell-name">
                        {name}
                    </div>
                    {Boolean(practiceInfo) && (
                        <div
                            className={classNames('spot-typography__text--tertiary', styles.practiceInfo)}
                            data-automation-id="practices-grid-cell-practice-info"
                        >
                            {practiceInfo}
                        </div>
                    )}
                </div>
            </div>
        );
    };
};

const PimsHeader = (): ReactElement => {
    // filter by pims should goes here
    return (
        <div className={styles.headerContainer}>
            <div className={classNames('spot-typography__text--body', styles.headerCell)}>{i18n.t('myOrganization:pims', 'PIMS')}</div>
        </div>
    );
};

const PimsCell = (params: GridCellParams): ReactElement => {
    const { pimsName } = params.row as SiteEntityInfo;

    return (
        <div className={styles.cellContainer}>
            <div className={classNames('spot-typography__text--body', styles.cell)}>
                <span data-automation-id="practices-grid-cell-pims-name">{pimsName}</span>
            </div>
        </div>
    );
};

const LabelsHeader = (): ReactElement => {
    // filter by labels should goes here
    return (
        <div className={styles.headerContainer}>
            <div className={classNames('spot-typography__text--body', styles.headerCell)}>{i18n.t('myOrganization:region', 'Region')}</div>
        </div>
    );
};
const LabelsCell = (params: GridCellParams): ReactElement => {
    const { labels } = params.row as SiteEntityInfo;
    const labelNames = labels?.map(({ name }) => name);

    return (
        <div className={styles.cellContainer}>
            <div className={classNames('spot-typography__text--body', styles.cell)}>
                {Boolean(labelNames?.length) && <span data-automation-id="practices-grid-cell-region-name">{labelNames?.join(', ')}</span>}
            </div>
        </div>
    );
};

const PracticeStatusHeader = (): ReactElement => {
    // filter by practice status should goes here
    return (
        <div className={styles.headerContainer}>
            <div className={classNames('spot-typography__text--body', styles.headerCell)}>
                {i18n.t('myOrganization:connectionStatus', 'Connection Status')}
            </div>
        </div>
    );
};

const practiceStatusCell = (props: PracticesGridProps) =>
    function PracticeStatusCell(params: GridCellParams): ReactElement {
        const practice = params.row as SiteEntityInfo;
        const { name, type, isActive, isAgentWorking, isDbWorking } = practice;

        const renderAppActiveSwitch = () => {
            const onChange = () => props.onActiveToggle?.(practice);
            return (
                <SpotToggle className={styles.toggle} checked={isActive} onChange={onChange} automationId={`toggle-active_${name.replace(/ /g, '')}`}>
                    {i18n.t('myOrganization:controlCenter', 'Control Center')}
                </SpotToggle>
            );
        };

        return (
            <div className={styles.cellContainer} data-automation-id="practices-grid-cell-connection-status">
                {type === PracticeStatus.ControlCenter && (
                    <>
                        {!isAgentWorking && <SpotPill color={styles.connectionErrorColor}>{i18n.t('myOrganization:offline', 'Offline')}</SpotPill>}
                        {isAgentWorking && !isDbWorking && (
                            <SpotPill color={styles.connectionErrorColor}>{i18n.t('myOrganization:databaseDown', 'Database Down')}</SpotPill>
                        )}
                        {isAgentWorking && isDbWorking && props.onActiveToggle && (
                            <>
                                <div className={classNames('spot-typography__text--body', styles.cell)}>{renderAppActiveSwitch()}</div>
                            </>
                        )}
                    </>
                )}
                {type === PracticeStatus.AnalyticsOnly && (
                    <div className={classNames('spot-typography__text--body', styles.cell)}>
                        {i18n.t('myOrganization:analyticsOnly', 'Analytics Only')}
                    </div>
                )}
                {type === PracticeStatus.Pending && (
                    <SpotPill color={styles.connectionPendingColor}>{i18n.t('myOrganization:connectionPending', 'Connection Pending')}</SpotPill>
                )}
            </div>
        );
    };

export type PracticesGridProps = PropsWithChildren<{
    practices: PracticeInfo[];
    className?: string;
    isLoading?: boolean;
    onActiveToggle?: (practice: PracticeInfo) => void;
    onSelectionChange?: (practice: PracticeInfo[]) => void;
    checkboxSelection?: boolean;
    isSyncPending: boolean;
    onSyncClick?: (siteId: string[] | '*') => void;
    masterSiteId?: string;
    groupBy?: string;
}>;

export const PracticesGrid = memo(function PracticesGrid(props: PracticesGridProps) {
    const { practices, className, isLoading, onSelectionChange, checkboxSelection, isSyncPending, masterSiteId, groupBy } = props;
    const { isTabletView, headerHeight, rowHeight } = usePracticesGridSize();

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: GridColumns.MobileView,
                flex: 1,
                renderHeader: MobileHeader,
                renderCell: mobileCell(props),
                valueGetter: (params: GridValueGetterParams) => {
                    return getPracticeName(params.row as PracticeInfo);
                },
            },
            {
                field: GridColumns.Name,
                flex: 2,
                renderHeader: nameHeader(),
                renderCell: desktopCell(),
                valueGetter: (params: GridValueGetterParams) => {
                    return getPracticeName(params.row as PracticeInfo);
                },
            },
            {
                field: GridColumns.Action,
                width: 160,
                sortable: false,
                hideable: false,
                resizable: false,
                editable: false,
                groupable: false,
                pinnable: false,
                renderHeader: actionHeader(props),
                renderCell: actionCell(props),
            },
            {
                field: GridColumns.PIMS,
                width: 230,
                renderHeader: PimsHeader,
                renderCell: PimsCell,
                valueGetter: (params: GridValueGetterParams) => {
                    const { pimsName } = params.row as SiteEntityInfo;
                    return pimsName;
                },
            },
            {
                field: GridColumns.Labels,
                flex: 1,
                renderHeader: LabelsHeader,
                renderCell: LabelsCell,
                valueGetter: (params: GridValueGetterParams) => {
                    const { labels } = params.row as SiteEntityInfo;
                    return head(labels)?.name;
                },
            },
            {
                field: GridColumns.PracticeStatus,
                width: 210,
                renderHeader: PracticeStatusHeader,
                renderCell: practiceStatusCell(props),
                valueGetter: (params: GridValueGetterParams) => {
                    const { type, isActive } = params.row as SiteEntityInfo;
                    return `${type}-${isActive}`;
                },
            },
        ],
        [isTabletView, isSyncPending, masterSiteId, groupBy],
    );

    const columnVisibilityModel: GridColumnVisibilityModel = useMemo(
        () => ({
            [GridColumns.MobileView]: isTabletView,
            [GridColumns.Name]: !isTabletView,
            [GridColumns.Action]: true,
            [GridColumns.PIMS]: !isTabletView,
            [GridColumns.Labels]: !isTabletView,
            [GridColumns.PracticeStatus]: !isTabletView,
        }),
        [isTabletView],
    );

    const getRowClassName = (params: GridRowParams): string => {
        const { type, isAgentWorking, isDbWorking } = params.row as SiteEntityInfo;
        const classes = [styles.row];

        if (type === PracticeStatus.ControlCenter && (!isAgentWorking || !isDbWorking)) {
            classes.push(styles.rowError);
        }

        return classNames(classes);
    };

    const isRowSelectable = (params: GridRowParams): boolean => {
        const { type } = params.row as PracticeInfo;
        return type === PracticeStatus.AnalyticsOnly || type === PracticeStatus.ControlCenter;
    };

    const getRowId = (row: GridRowModel): string => {
        const { id, accountId } = row as PracticeInfo;
        return `${id}-${accountId}`;
    };

    const onSelectionModelChange = (selectionModel: GridRowSelectionModel): void => {
        const selectedPractices = practices.filter((practice) => selectionModel.includes(getRowId(practice)));

        onSelectionChange?.(selectedPractices);
    };

    return (
        <MaterialDataGrid
            loading={isLoading}
            className={className}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            rows={practices}
            headerHeight={headerHeight}
            rowHeight={rowHeight}
            checkboxSelection={checkboxSelection}
            getRowClassName={getRowClassName}
            isRowSelectable={isRowSelectable}
            onRowSelectionModelChange={onSelectionModelChange}
            autoHeight={true}
            getRowId={getRowId}
            disableRowSelectionOnClick={true}
        />
    );
});
